import React from 'react';
import { Box, Button, FormControl, MenuItem, Select } from '@mui/material';
import { toSentenceCase, toTitleCase } from '../../../utils/casing';

const KeyboardArrowDownIcon =
  'https://assets.askhippo.co.za/Icons/common/KeyboardDownArrow_Black.svg';
const DoubleArrowIcon =
  'https://assets.askhippo.co.za/Icons/common/DoubleArrow_White.svg';

// The closed dropdown and the button are mirrored in src/staticHero/staticHero.css
// (docs/static-hero.md): change both, or the page jumps when React takes over.
const QuoteSelectorComponent = ({
  value,
  onChange,
  onQuote,
  options = [],
  placeholder,
  isMobile,
  buttonLabel,
  hideDropdown = false,
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row', // row on desktop
        alignItems: 'center',
        justifyContent: isMobile
          ? 'center'
          : hideDropdown
            ? 'center'
            : 'flex-start',
        gap: isMobile ? 2 : '16px', // spacing
        width: '100%',
      }}
    >
      {!hideDropdown && (
        /* Dropdown capsule */
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            borderRadius: '16px',
            width: isMobile ? '100%' : '464px', // fixed width on desktop
            height: '48px',
            backgroundColor: '#FFFFFF',
            border: '1px solid #A2ACB1',
            px: 2,
            flexShrink: 0,
          }}
        >
          <FormControl
            variant="outlined"
            sx={{
              flex: 1,
              width: '100%',
              height: '100%',
              justifyContent: 'center',
              '& .MuiOutlinedInput-root': {
                height: '100%',
                border: 'none',
                paddingLeft: 0,
                '& fieldset': {
                  border: 'none',
                },
              },
            }}
          >
            <Select
              native={false}
              displayEmpty
              value={value}
              onChange={(e) => onChange(e.target.value)}
              inputProps={{ 'aria-label': placeholder }}
              renderValue={(selected) =>
                selected ? (
                  <span style={{ color: '#738289' }}>
                    {toTitleCase(selected)}
                  </span>
                ) : (
                  <span style={{ color: '#738289' }}>{placeholder}</span>
                )
              }
              IconComponent={(props) => (
                <span
                  {...props}
                  style={{
                    fontSize: '1.5rem',
                    width: '1em',
                    height: '1em',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '8px',
                    cursor: 'pointer',
                  }}
                >
                  <img
                    src={KeyboardArrowDownIcon}
                    alt="Down arrow icon"
                    width={24}
                    height={24}
                    style={{ display: 'block' }}
                  />
                </span>
              )}
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: '#FFFFFF',
                    borderRadius: '16px',
                    position: 'fixed',
                    minWidth: '211px',
                    left: '-16px',
                    border: '1px solid #A2ACB1',
                    boxShadow:
                      '0px 6px 16px 0px #162F3B1F, 0px 1px 2px 0px #A2ACB11F',
                    mt: 1,
                    px: 0.5,
                    py: 1,
                    maxHeight: '260px',
                    overflowY: 'auto',
                    scrollbarWidth: 'none',
                    '&::-webkit-scrollbar': {
                      display: 'none',
                    },
                  },
                },
              }}
              sx={{
                fontFamily: 'Inter, Inter-Fallback, sans-serif',
                fontWeight: 600,
                fontSize: '0.9375rem',
                lineHeight: '1.125rem',
                letterSpacing: '-0.02em',
                color: '#738289',
                pl: 1,
                height: '100%',
                cursor: 'pointer',
                '& .MuiSelect-select': {
                  display: 'flex',
                  alignItems: 'center',
                },
              }}
            >
              {options.map((opt, idx) => (
                <MenuItem
                  key={idx}
                  value={opt}
                  sx={{
                    fontFamily: 'Inter, Inter-Fallback, sans-serif',
                    fontWeight: 400,
                    fontSize: '0.875rem',
                    lineHeight: '1.25rem',
                    color: '#1C2F3A',
                    py: 1.5,
                    px: 2,
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                    },
                    '&.Mui-selected': {
                      backgroundColor: '#f0f0f0 !important',
                      color: '#1C2F3A',
                    },
                    '&.Mui-selected:hover': {
                      backgroundColor: '#f0f0f0 !important',
                    },
                  }}
                >
                  {toTitleCase(opt)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {/* Button next to dropdown on desktop / below on mobile */}
      <Button
        variant="contained"
        onClick={onQuote}
        disabled={!value}
        sx={{
          width: hideDropdown ? 'auto' : isMobile ? '100%' : '179px',
          minWidth: hideDropdown ? '162px' : isMobile ? '100%' : '179px',
          height: '48px',
          borderRadius: '20px',
          backgroundColor: '#FF007A',
          padding: hideDropdown ? '12px 24px' : isMobile ? '0' : '12px 24px',
          fontFamily: 'Inter, Inter-Fallback, sans-serif',
          fontWeight: 600,
          color: '#FFFFFF',
          fontSize: isMobile ? '1rem' : '1.25rem',
          textTransform: 'none',
          flexShrink: 0,
          '&:hover': {
            backgroundColor: '#e6006d',
          },
        }}
      >
        {toSentenceCase(buttonLabel)}
        <img
          src={DoubleArrowIcon}
          alt="Double arrow icon"
          width={24}
          height={24}
          style={{ display: 'block', opacity: !value ? 0.4 : 1 }}
        />
      </Button>
    </Box>
  );
};

export default QuoteSelectorComponent;
